import React, { useEffect, useRef } from "react";
import { useFieldArray, useFormContext, useFormState, useWatch } from "react-hook-form";
import StepHeader from "../StepHeader";
import EntryCard from "../EntryCard";
import RHFInput from "@/components/form/RHFInput";
import RHFTextArea from "@/components/form/RHFTextArea";
import RHFMonthYearPicker from "@/components/form/RHFMonthYearPicker";
import { calculateTotalExperience } from "../../utils/calculateExperience";

const MAX_COMPANY_LEN = 75;
const MAX_JOB_TITLE_LEN = 75;
const MAX_LOCATION_LEN = 100;

const StepExperience = ({ mandatoryFields = [], nextAttempt = 0, autoCalculate = true, disabled = false }) => {
  const { control, setValue, trigger } = useFormContext();
  const { errors } = useFormState({ control, name: "experience" });
  const hasAppended = useRef(false);
  // Track whether the user has manually edited total_experience
  const isManualOverride = useRef(false);

  const isJobTitleRequired = mandatoryFields.some((f) => f === "work_experience.title" || f === "experience.jobTitle" || f === "jobTitle");
  const isCompanyRequired = mandatoryFields.some((f) => f === "work_experience.company" || f === "experience.company" || f === "company");
  const isStartDateRequired = mandatoryFields.some((f) => f === "work_experience.start_date" || f === "experience.startDate" || f === "startDate");
  const isEndDateRequired = mandatoryFields.some((f) => f === "work_experience.end_date" || f === "experience.endDate" || f === "endDate");
  const isLocationRequired = mandatoryFields.some((f) => f === "work_experience.location" || f === "experience.location" || f === "location");
  const isDescriptionRequired = mandatoryFields.some((f) => f === "work_experience.description" || f === "experience.description" || f === "description");

  const { fields, append, remove } = useFieldArray({ control, name: "experience" });

  useEffect(() => {
    if (fields.length === 0 && !hasAppended.current) {
      hasAppended.current = true;
      append({ jobTitle: "", company: "", startDate: "", endDate: "", location: "", description: "", achievements: [] });
    }
  }, []);

  const watchedExperience = useWatch({ control, name: "experience" });

  // Watch date fields explicitly so changes trigger recalculation
  const watchedStartDates = useWatch({
    control,
    name: fields.map((_, i) => `experience.${i}.startDate`),
  });
  const watchedEndDates = useWatch({
    control,
    name: fields.map((_, i) => `experience.${i}.endDate`),
  });

  useEffect(() => {
    if (!fields.length) return;

    fields.forEach((_, index) => {
      trigger(`experience.${index}.startDate`);
      trigger(`experience.${index}.endDate`);
    });
  }, [watchedStartDates, watchedEndDates, fields.length, trigger]);

  useEffect(() => {
    if (!autoCalculate) return;
    if (isManualOverride.current) return;
    const calculated = calculateTotalExperience(watchedExperience);
    if (calculated !== "") {
      setValue("compensation.total_experience", String(calculated), { shouldDirty: true });
    }
  }, [
    autoCalculate,
    watchedExperience,
    watchedStartDates,
    watchedEndDates,
    setValue
  ]);
  // ↑ adding the flat date arrays as deps forces re-run on every date change

  const hasMandatoryExperienceFields =
    isJobTitleRequired || isCompanyRequired || isStartDateRequired || isEndDateRequired || isLocationRequired || isDescriptionRequired;

  return (
    <div>
      <StepHeader
        title="Work Experience" disabled={disabled}
        onAdd={() => append({ jobTitle: "", company: "", startDate: "", endDate: "", location: "", description: "", achievements: [] })}
      />

      <div className="space-y-5 px-2">
        {fields.map((item, index) => {
          const currentExperience = 
          watchedExperience?.[index] ?? item;
          
          return (
            <EntryCard
              key={item.id}
              label={currentExperience?.company}
              forceOpen={!!errors?.experience?.[index]}
              forceOpenTrigger={nextAttempt}
              defaultOpen={!currentExperience?.company}
              canRemove={hasMandatoryExperienceFields ? index !== 0 : fields.length > 1}
              onRemove={() => remove(index)}
              disabled={disabled}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <RHFInput name={`experience.${index}.company`} label="Company / Organization" placeholder="Enter your Company" maxLength={MAX_COMPANY_LEN} required={isCompanyRequired} disabled={disabled} />
                <RHFInput name={`experience.${index}.jobTitle`} label="Job Title / Role" placeholder="Not Specified" maxLength={MAX_JOB_TITLE_LEN} required={isJobTitleRequired} disabled={disabled} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <RHFMonthYearPicker name={`experience.${index}.startDate`} label="Start Date" placeholder="MM-YYYY" required={isStartDateRequired} disabled={disabled} />
                <RHFMonthYearPicker name={`experience.${index}.endDate`} label="End Date" placeholder="MM-YYYY" allowPresent required={isEndDateRequired} disabled={disabled} />
              </div>

              <RHFInput name={`experience.${index}.location`} label="Job Location" placeholder="City, Country" maxLength={MAX_LOCATION_LEN} required={isLocationRequired} disabled={disabled} />
              <RHFTextArea name={`experience.${index}.description`} label="Responsibilities / Description" placeholder="Write down what you did during this time at this job..." required={isDescriptionRequired} disabled={disabled} />
            </EntryCard>
          );
        })}
      </div>
    </div>
  );
};

export default StepExperience;